import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select from 'react-select';
import classNames from 'classnames';
import { FaPlus } from 'react-icons/fa6';
import { ExcelColumn, ExcelColumnType } from '../../../../types/misc';
import { LocalContactInfo } from '../../../../models/localContactService.model';
import { CommunityInvite, CustomFieldType } from '../../../../models/inviteService.model';
import { ProfileDataType } from '../../../../types/Profile';
import {
  formatLocalContactInfosfromColumns,
  fromInviteToExcelColumnTypes,
  fromStringtoExcelColumnType,
  createEmptyCommunityInvite
} from '../../../../utils/formatUtils';
import { filterCustomLabelFields } from '../../../../utils/filterUtils';
import { excelColumnTypeToString } from '../../../../utils/stringUtils';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_300,
  COLOR_PRIMARY_900,
  COLOR_SECONDARY_50,
} from '../../../../constants';
import CancelSaveButton from '../../misc/CancelSaveButton';
import PageHeader from '../../misc/PageHeader';
import ModalLayout from '../../layouts/ModalLayout';
import InviteForm from '../../forms/InviteForm';
import inviteService from '../../../../services/community/inviteService';
import connectionService from '../../../../services/community/connectionService';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import dashboardService from '../../../../services/community/communityService';
import Button from '../../buttons/Button';

interface SelectColumnsHeaderProps {
  fileName: string;
}

export function SelectColumnsHeader({ fileName }: SelectColumnsHeaderProps): JSX.Element {
  const { t } = useTranslation('', {
    keyPrefix: 'component.modal.importLocalContacts.selectColumn',
  });

  return (
    <PageHeader
      title={t('title', { fileName })}
      subtitle={t('subtitle')}
    />
  );
}

interface SelectColumnsViewProps {
  columns: ExcelColumn[];
  setColumns: (columns: ExcelColumn[]) => void;
  setLocalContacts: (contacts: LocalContactInfo[]) => void;
  invite: CommunityInvite;
  fileName: string;
  onSubmitSuccessful?: () => void;
  onSubmitFailed?: () => void;
  onMissingCountry?: () => void;
}

export function SelectColumnsView({
  columns,
  setColumns,
  setLocalContacts,
  invite,
  fileName,
  onSubmitSuccessful,
  onSubmitFailed,
  onMissingCountry,
}: SelectColumnsViewProps): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();

  const [columnTypes, setColumnTypes] = useState<(ExcelColumnType | string)[]>([]);
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [modalInvite, setModalInvite] = useState<CommunityInvite>(createEmptyCommunityInvite());
  const [isSavingFields, setIsSavingFields] = useState(false);
  const [currentInvite, setCurrentInvite] = useState<CommunityInvite>(invite);

  useEffect(() => {
    setCurrentInvite(invite);
  }, [invite]);

  const includeColumnType = (type: ExcelColumnType | string) => {
    const dataRequest = [...currentInvite.mandatoryFields, ...currentInvite.optionalFields];
    switch (type) {
      case ExcelColumnType.EMAIL:
        return dataRequest.includes(ProfileDataType.EMAIL);
      case ExcelColumnType.PHONENUMBER:
        return dataRequest.includes(ProfileDataType.PHONENUMBER);
      case ExcelColumnType.STREET:
      case ExcelColumnType.POSTAL_CODE:
      case ExcelColumnType.CITY:
      case ExcelColumnType.COUNTRY:
        return dataRequest.includes(ProfileDataType.ADDRESS);
      case ExcelColumnType.BIRTHDATE:
        return dataRequest.includes(ProfileDataType.BIRTHDATE);
      case ExcelColumnType.BUSINESSNAME:
        return dataRequest.includes(ProfileDataType.BUSINESSNAME);
      case ExcelColumnType.COMMUNICATION_NAME:
        return currentInvite.customFields.find((f) => f.type === CustomFieldType.COMMUNICATION_NAME);
      default:
        return true;
    }
  };

  useEffect(() => {
    const isEmptyColumn = (col: ExcelColumn) =>
      col.rows.every((r) => r === null || r === undefined || String(r).trim() === '');

    const nonEmptyIndices = columns
      .map((c, idx) => ({ c, idx }))
      .filter(({ c }) => !isEmptyColumn(c))
      .map(({ idx }) => idx);

    if (nonEmptyIndices.length === columns.length) return;

    const filtered = nonEmptyIndices.map((idx) => columns[idx]);

    setColumnTypes((prev) => {
      const newTypes: (ExcelColumnType | string)[] = [];
      nonEmptyIndices.forEach((oldIdx, newIdx) => {
        if (prev[oldIdx] !== undefined) newTypes[newIdx] = prev[oldIdx];
      });
      return newTypes;
    });

    setColumns(filtered);
  }, [columns, setColumns]);

  useEffect(() => {
    const newColumnTypes = [...columnTypes];
    columns.forEach((c, i) => {
      const type =
        fromStringtoExcelColumnType(c.name) ||
        filterCustomLabelFields(currentInvite.customFields).find(
          (f) => f.label.toLowerCase() === c.name.toLowerCase(),
        )?.label;

      const isInvalid = type === undefined || Object.values(columnTypes).includes(type);

      if (isInvalid) return;

      if (includeColumnType(type)) {
        newColumnTypes[i] = type;
      }
    });
    setColumnTypes(newColumnTypes);
  }, []);

  const deleteColumn = (index: number) => {
    const newColumns = [...columns];
    newColumns.splice(index, 1);
    const newColumnTypes = { ...columnTypes };
    delete newColumnTypes[index];
    Object.keys(newColumnTypes).forEach((k) => {
      if (+k > index) {
        newColumnTypes[+k - 1] = newColumnTypes[+k];
        delete newColumnTypes[+k];
      }
    });
    setColumnTypes(newColumnTypes);
    setColumns(newColumns);
  };

  const handleSelect = (v: any, index: number) => {
    if (v?.value === '__ADD_FIELD__') {
      openFieldsModal();
      return;
    }
    
    //set all other columns equal to v.value in columnTypes to undefined
    const newColumnTypes = { ...columnTypes };
    Object.keys(columnTypes).forEach((k) => {
      if (columnTypes[+k] === v?.value) delete newColumnTypes[+k];
    });
    newColumnTypes[index] = v?.value as ExcelColumnType;
    setColumnTypes(newColumnTypes);
  };

  const handleSubmit = async () => {
    const typeValues = Object.values(columnTypes);
    if (
      typeValues.includes(ExcelColumnType.INFIX) &&
      !typeValues.includes(ExcelColumnType.LAST_NAME)
    ) {
      toast.error(t('toast.error.infixWithoutLastName'));
      return;
    }

    if (
      typeValues.includes(ExcelColumnType.FULL_NAME) &&
      (typeValues.includes(ExcelColumnType.FIRST_NAME) ||
        typeValues.includes(ExcelColumnType.LAST_NAME))
    ) {
      toast.error(t('toast.error.fullNameWithFirstNameOrLastName'));
      return;
    }

    const contacts = formatLocalContactInfosfromColumns(columns, columnTypes, currentInvite.customFields);

    if (
      (typeValues.includes(ExcelColumnType.STREET) ||
        typeValues.includes(ExcelColumnType.POSTAL_CODE) ||
        typeValues.includes(ExcelColumnType.CITY)) &&
      !typeValues.includes(ExcelColumnType.COUNTRY)
    ) {
      // ask the user to provide a country for all addresses first
      setLocalContacts(contacts);
      onMissingCountry?.();
      return;
    }

    // Always go to review screen to let users see and fix any remaining issues
    setLocalContacts(contacts);
  };

  const openFieldsModal = async () => {
    setShowFieldsModal(true);
    const fields = await inviteService.fetchInvite();
    if (fields) {
      setModalInvite(fields);
    }
  };

  const handleSaveFields = async () => {
    const labelFields = filterCustomLabelFields(modalInvite.customFields);
    if (
      labelFields.some((field): boolean => !field.label) ||
      modalInvite.customFields
        .filter((x) => x.type === CustomFieldType.MULTIPLE_CHOICE)
        .map((x) => (x as any).options)
        .flat()
        .some((option): boolean => !option)
    ) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    if (
      Array.from(new Set(labelFields.map((field) => field.label))).length !== labelFields.length
    ) {
      toast.error(t('toast.error.noTwoSame'));
      return;
    }

    setIsSavingFields(true);
    try {
      await inviteService.updateInvite(modalInvite);
      await connectionService.getConnections(true);
      
      if (modalInvite.nonRequestedFields.length !== 10) {
        const features = { ...(community?.features || {}) };
        if (!features.MEMBER_ADMINISTRATION) {
          features.MEMBER_ADMINISTRATION = true;
          await dashboardService.updateCommunity(
            community?.name || '',
            community?.description || '',
            community?.memberPermissions || {},
            features,
          );
        }
      }

      await dashboardService.fetchCommunity();
      
      const updatedInvite = await inviteService.fetchInvite();
      if (updatedInvite) {
        setCurrentInvite(updatedInvite);
      }
      
      toast.success(t('toast.success.inviteUpdated'));
      setShowFieldsModal(false);
    } catch (error) {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setIsSavingFields(false);
    }
  };

  const options = fromInviteToExcelColumnTypes(currentInvite)
    .map((k) => ({
      label: excelColumnTypeToString(k as ExcelColumnType),
      value: k as string,
    }))
    .concat(
      filterCustomLabelFields(currentInvite.customFields).map((f) => ({
        label: f.label,
        value: f.label,
      })),
    )
    .concat([
      {
        label: `+ ${t('component.modal.importLocalContacts.selectColumn.addFieldOption')}`,
        value: '__ADD_FIELD__',
      },
    ]);

  return (
    <>
      <SelectColumnsHeader fileName={fileName} />

      <div className="flex flex-col gap-2 mb-2">
        <p className="max-w-2xl text-sm text-gray-600">
          {t('component.modal.importLocalContacts.selectColumn.addFieldDescription')}
        </p>
        <div className="flex-shrink-0">
          <Button variant="primary" onClick={openFieldsModal} className="px-4 py-2">
            {t('component.modal.importLocalContacts.selectColumn.addField')}
          </Button>
        </div>
      </div>

      <div className="flex w-full flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {columns.map(
            (c: ExcelColumn, i: number): JSX.Element => (
              <div key={i} className="flex h-fit flex-col overflow-hidden rounded-2xl bg-secondary-200/50">
                <div className="flex flex-col gap-3 rounded-t-2xl bg-secondary-100 p-5">
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-medium text-gray-500">
                      {t('component.modal.importLocalContacts.selectColumn.columnName')}
                    </p>
                    <p className="w-fit rounded-lg bg-primary-300 px-3 py-1.5 text-sm font-semibold text-primary-900">
                      {c.name && c.name.includes('__EMPTY') ? t('component.modal.importLocalContacts.selectColumn.emptyColumn') : `"${c.name}"`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                    key={i}
                    // render dropdown into document.body so it won't be clipped by parent overflow
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    styles={{
                      control: (baseStyles: any, state) => ({
                        ...baseStyles,
                        borderColor: COLOR_PRIMARY,
                        borderRadius: '9999px',
                        color: COLOR_PRIMARY,
                        backgroundColor: COLOR_SECONDARY_50,
                        boxShadow: 'none',
                        minHeight: '42px',
                      }),
                      option: (styles: any, { isSelected, data }) => {
                        const isAddOption = data.value === '__ADD_FIELD__';
                        return {
                          ...styles,
                          backgroundColor:  
                            isSelected 
                              ? COLOR_PRIMARY 
                              : COLOR_SECONDARY_50,
                          color: isAddOption 
                            ? COLOR_PRIMARY 
                            : isSelected 
                              ? COLOR_SECONDARY_50 
                              : COLOR_PRIMARY_900,
                          fontWeight: isAddOption ? 600 : 'normal',
                          minHeight: '40px',
                        };
                      },
                      indicatorSeparator: () => ({
                        display: 'none',
                      }),
                      dropdownIndicator: (baseStyles: any, state) => ({
                        ...baseStyles,
                        color: state.isFocused ? COLOR_PRIMARY_300 : COLOR_PRIMARY,
                      }),
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    options={options}
                    value={
                      columnTypes[i]
                        ? {
                          label: excelColumnTypeToString(columnTypes[i] as ExcelColumnType),
                          value: columnTypes[i] as string,
                        }
                        : null
                    }
                    onChange={(v: any) => handleSelect(v, i)}
                    className="mb-2 flex-1 min-w-0"
                    placeholder={t('component.modal.importLocalContacts.selectColumn.selectPlaceholder')}
                  />
                    <button
                      type="button"
                      onClick={openFieldsModal}
                      aria-label={t('component.modal.importLocalContacts.selectColumn.addField')}
                      className="mb-2 flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full bg-primary-300 text-primary"
                      title={t('component.modal.importLocalContacts.selectColumn.addField')}>
                      <FaPlus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Columns */}
                <div className="flex max-h-40 flex-col overflow-y-auto">
                  {c.rows.map((r, j) => (
                    <div
                      key={j}
                      className={classNames('block px-4 py-2.5 bg-secondary-200 text-sm text-gray-700', {
                        'bg-secondary-50': j % 2 !== 0,
                      })}>
                      {r || '-'}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end rounded-b-2xl bg-secondary-100 p-4">
                  <button
                    type="button"
                    onClick={() => deleteColumn(i)}
                    className="text-sm font-semibold text-danger underline decoration-danger underline-offset-4 transition-colors">
                    {t('component.modal.importLocalContacts.selectColumn.dontImport')}
                  </button>
                </div>
              </div>
            ),
          )}
        </div>

        {/* Buttons */}
        <div className="flex w-full justify-end pt-6">
          <CancelSaveButton
            onCancel={() => onSubmitFailed?.()}
            onSave={handleSubmit}
            disabled={Object.keys(columnTypes).length !== columns.length}
          />
        </div>
      </div>

      {/* Add/Edit Fields Modal */}
      <ModalLayout
        open={showFieldsModal}
        setOpen={setShowFieldsModal}
        closeButton
        className="z-50 mx-6 my-8 w-full overflow-y-auto rounded-lg bg-secondary-50 p-6 sm:max-w-[900px]">
        <div className="flex flex-col">
          <div className="mb-6 flex flex-col border-b border-primary-300 pb-6">
            <h2 className="mb-2 text-2xl font-semibold">
              {t('page.requestedData.title')}
            </h2>
            <p className="text-sm">
              {t('page.requestedData.subtitle')}
            </p>
          </div>

          <div className="mb-6">
            <InviteForm invite={modalInvite} setInvite={setModalInvite} />
          </div>

          <div className="flex justify-end gap-3 border-t border-primary-300 pt-6">
            <Button
              onClick={() => setShowFieldsModal(false)}
              disabled={isSavingFields}
              className="rounded-[12px] bg-secondary-200 px-6 py-2.5 text-sm font-semibold disabled:opacity-50">
              {t('general.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveFields}
              disabled={isSavingFields}
              className="rounded-[12px] px-6 py-2.5 text-sm font-semibold disabled:opacity-50">
              {isSavingFields ? t('general.saving') : t('general.save')}
            </Button>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}
